// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useEffect, useMemo, useRef, type ReactNode } from 'react';

import {
  type EvolveApiRoute,
  type RouteWithPathParam,
  type GetOpts,
  useEvolveGet,
  type ProductId,
} from '@evolvemep/api-ts';

import { type LOCAL_STORAGE_KEY, PRODUCT_IDS } from 'constants/globalConstants';
import { isNil } from 'helpers/isNotNil';
import { strongIsEqual } from 'helpers/strongEntries';
import { useGeneralContext } from 'helpers/useGeneralContext';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useMaybeUser } from 'hooks/user/useMaybeUser';

type EvolveGetWithLocalStorageContextType = {
  alreadyPulled: React.RefObject<Partial<Record<LOCAL_STORAGE_KEY, boolean>>>;
};

const EvolveGetWithLocalStorageContext = React.createContext<EvolveGetWithLocalStorageContextType | undefined>(
  undefined,
);

export const EvolveGetWithLocalStorageProvider = ({ children }: { children: ReactNode }) => {
  const alreadyPulled = useRef<Partial<Record<LOCAL_STORAGE_KEY, boolean>>>({});
  return <EvolveGetWithLocalStorageContext value={{ alreadyPulled }}>{children}</EvolveGetWithLocalStorageContext>;
};

/**
 * This context tracks promises that will update the local storage cache.
 * Generally, you'll check `alreadyPulled.current[localStorageKey]` to see if the API call has already been made.
 * If it has, bail out. Then, before your promise executes, set `alreadyPulled.current[localStorageKey] = true`.
 * If your promise is rejected, set `alreadyPulled.current[localStorageKey] = false`.
 * USE WITH CAUTION. DO NOT USE OUTSIDE OF CASES THAT UPDATE THE LOCAL STORAGE CACHE ASYNCHRONOUSLY.
 *
 * TODO: extract more of the logic for this so callers don't need to know so much about it to use it.
 *
 * @returns The context for useEvolveGetWithLocalStorage, which is a Ref object for with a record of local storage keys that have already been pulled.
 *  The record should be updated to `true` for that key when a call is made, and set to `false` if the call fails.
 */
export const useEvolveGetWithLocalStorageContext = () =>
  useGeneralContext(EvolveGetWithLocalStorageContext, 'EvolveGetWithLocalStorage');

export type ValidCacheableRoute = Exclude<EvolveApiRoute<'GET'>, RouteWithPathParam>;

type Opts<Route extends ValidCacheableRoute> = {
  /**
   * The API call will only be made if the user has this license.
   * If `null`, the API call will always be made.
   *
   * @defaultValue PRODUCT_IDS.SHOP_LICENSE
   */
  requireLicense?: ProductId | null;
  /**
   * If true, will delay the API call if we already have a cached value.
   *
   * @defaultValue true
   */
  delay?: boolean;
} & (
  | {
      defaultOpts?: never;
      appendKey?: never;
    }
  | {
      /** API call GET opts */
      defaultOpts: GetOpts<Route>;
      /**
       * A key to append to the storage cache key.
       * Used when the same endpoint is used for multiple caches.
       * (for example, WO and Task custom properties)
       */
      appendKey: string;
    }
);

/**
 * Returns a response cached in local storage and will update the local storage
 * if the data retrieved from the network is different from the cache.
 */
export const useEvolveGetWithLocalStorage = <Route extends ValidCacheableRoute>(url: Route, opts: Opts<Route> = {}) => {
  const { defaultOpts, appendKey = '_', requireLicense = PRODUCT_IDS.SHOP_LICENSE, delay = true } = opts;
  const localStorageKey = useMemo(() => `EVOLVE_GET_CACHE_${url}_${appendKey}` as const, [url, appendKey]);
  const { alreadyPulled } = useEvolveGetWithLocalStorageContext();

  const { licenses } = useMaybeUser();
  const [stored, setStored, hasStoredValue] = useLocalStorage(localStorageKey, undefined);
  const { apiCall, loading, ...rest } = useEvolveGet<Route>(url, { lazy: true, ...defaultOpts });

  useEffect(() => {
    if (isNil(requireLicense) || licenses.includes(requireLicense)) {
      setTimeout(
        () => {
          if (alreadyPulled.current[localStorageKey]) return;

          alreadyPulled.current[localStorageKey] = true;
          /* `apiCall` thinks that you may need to pass in args
           * in the case that `URL` is a parameterized URL missing its params.
           * However, we've excluded those from `URL`
           * so any params have already been passed into the `useEvolveGet` above. */
          // oxlint-disable-next-line typescript/no-explicit-any
          void apiCall(...([] as any))
            .then((data) => {
              /*
               * This prevents a serious bug that can occur when we cache an HTML file,
               * which can happen if our CDN serves the wrong files.
               *
               * If an HTML value is cached when we're expecting JSON, it will crash the entire app,
               * and prevent the cache from being evicted, which causes an infinite crash loop.
               */
              if (typeof data === 'string' && data.startsWith('<')) {
                throw new Error('Invalid HTML response');
              }
              setStored((s) => {
                if (strongIsEqual(data as object, s as object)) return s;
                return data as typeof s;
              });
            })
            .catch(() => (alreadyPulled.current[localStorageKey] = false));
        },
        // If we have a cached value already,
        // delay the load to allow other API calls to run first
        // (with a random delay to stagger the API calls).
        hasStoredValue && delay ? 3000 + Math.random() * 1000 : 0,
      );
    }
  }, [alreadyPulled, apiCall, delay, hasStoredValue, licenses, localStorageKey, setStored, requireLicense]);

  return useMemo(
    () => ({
      ...rest,
      loading: loading && !hasStoredValue,
      apiCall,
      data: stored,
      updateStoredValue: setStored,
    }),
    [rest, loading, stored, apiCall, setStored, hasStoredValue],
  );
};
